/**
 * The arithmetic of playing a transcript: which turn the clock is inside, how far through it is,
 * and where the reading column has to be for that turn to be under the reader's eye.
 *
 * **One clock.** The transcript, the activity timeline and the player bar all read `currentTime`
 * and all of them may write to it. Everything derived from that one number lives here as a pure
 * function, so the same position produces the same highlight whether it arrived from a press on a
 * turn, a drag of the scrubber, a click on the timeline, or the recording simply playing on.
 *
 * Nothing here touches an element or the DOM — the hook owns the audio, the components own the
 * pixels, and this module owns the answers both of them need.
 */

import type { TranscriptTurn } from "./transcript-turns-view";

/** The speeds the player offers, slowest first. `1` is the default and the middle of the ladder. */
export const PLAYBACK_RATES = [0.75, 1, 1.25, 1.5, 1.75, 2] as const;

export type PlaybackRate = (typeof PLAYBACK_RATES)[number];

/** The default speed, and the one a stored value that is not on the ladder falls back to. */
export const DEFAULT_PLAYBACK_RATE: PlaybackRate = 1;

/**
 * A turn placed in time.
 *
 * `endSeconds` is `null` where the vendor gave no end: that is "unmeasured", never "zero length" —
 * a turn drawn as instantaneous would fill its rail the moment it became active and claim a
 * progress nothing recorded.
 */
export type PlayableTurn = {
  /** The turn's own key — the block index it started at. */
  key: number;
  startSeconds: number;
  endSeconds: number | null;
};

/** How far either side of the clock a boundary has to be to count as a different one. */
const BOUNDARY_EPSILON_SECONDS = 0.25;

/**
 * What every disabled control says when this browser cannot decode the recording's audio.
 *
 * One sentence, stated once, because the play button and every per-turn glyph explain the same
 * thing. It names the fix rather than the codec: "Opus in Ogg" is not something a person can act
 * on, and a newer browser is.
 */
export const PLAYBACK_UNSUPPORTED_FORMAT_MESSAGE =
  "This browser can't play the recording's audio. Update your browser to listen here.";

/**
 * The turns a player can act on, in the order they were spoken.
 *
 * A turn with no start is on no clock, exactly as it is on no timeline track
 * (`buildSpeakerActivity`): a position nothing measured is not a position.
 */
export function playableTranscriptTurns(turns: readonly TranscriptTurn[]): PlayableTurn[] {
  const playable: PlayableTurn[] = [];
  for (const turn of turns) {
    const startSeconds = turn.header?.startSeconds;
    if (typeof startSeconds !== "number" || !Number.isFinite(startSeconds)) continue;
    const endSeconds = turn.header?.endSeconds;
    playable.push({
      key: turn.key,
      startSeconds: Math.max(0, startSeconds),
      endSeconds:
        typeof endSeconds === "number" && Number.isFinite(endSeconds) && endSeconds > startSeconds
          ? endSeconds
          : null,
    });
  }
  return playable;
}

/**
 * The turn the clock is in, or `null` before the first one starts.
 *
 * Exactly one turn or none, always: between two turns the clock belongs to the **last turn that
 * started**, because a gap is silence *after* somebody spoke rather than a gap belonging to
 * nobody, and a highlight that blinked off in every pause would be noise down a whole meeting.
 */
export function resolveActiveTurn(
  turns: readonly PlayableTurn[],
  currentTime: number
): PlayableTurn | null {
  let active: PlayableTurn | null = null;
  for (const turn of turns) {
    if (turn.startSeconds > currentTime) continue;
    if (active === null || turn.startSeconds >= active.startSeconds) active = turn;
  }
  return active;
}

/**
 * How far through one turn the clock is, `0`–`1`.
 *
 * A turn whose end was never measured reports `0`: the rail then stays at the speaker's pale tint
 * for that turn rather than drawing a fill nothing knows the length of.
 */
export function resolveTurnProgress(turn: PlayableTurn, currentTime: number): number {
  if (turn.endSeconds === null) return 0;
  const span = turn.endSeconds - turn.startSeconds;
  if (span <= 0) return 0;
  return Math.min(1, Math.max(0, (currentTime - turn.startSeconds) / span));
}

/** Where the reading column's anchor line sits, measured down from the top of the column. */
export const FOLLOW_ANCHOR_OFFSET = 100;

/**
 * The scroll offset that puts the turn being spoken where the reader is looking.
 *
 * Two shapes, because a turn either fits on the screen or it does not:
 *
 * - **It fits.** The turn's top goes to the anchor line, a little below the top edge, and stays
 *   there for the whole turn. Nothing moves while somebody speaks a paragraph.
 * - **It does not.** A twenty-minute monologue cannot sit still and be readable, so the column
 *   travels through it at the rate it is being spoken — the interpolation is the whole point, and
 *   the clamp at the end is what stops the block scrolling past its own last line.
 */
export function resolveFollowScrollTop(args: {
  /** The block's top, in the scroll container's own coordinates. */
  blockTop: number;
  blockHeight: number;
  viewportHeight: number;
  progress: number;
  anchorOffset?: number;
}): number {
  const anchorOffset = args.anchorOffset ?? FOLLOW_ANCHOR_OFFSET;
  if (args.blockHeight <= args.viewportHeight) {
    return Math.max(0, args.blockTop - anchorOffset);
  }
  const travel = args.blockHeight - args.viewportHeight;
  const progress = Math.min(1, Math.max(0, args.progress));
  return Math.max(0, Math.min(args.blockTop + travel, args.blockTop + progress * travel));
}

/**
 * The start of the turn before or after the clock, or `null` where there is none.
 *
 * The epsilon is what makes "previous" mean the previous turn rather than the start of the one
 * being spoken: a press a fraction of a second into a turn is a press asking to go back further.
 */
export function nextTurnBoundary(
  turns: readonly PlayableTurn[],
  currentTime: number,
  direction: "forward" | "back"
): number | null {
  let best: number | null = null;
  for (const turn of turns) {
    if (direction === "forward") {
      if (turn.startSeconds <= currentTime + BOUNDARY_EPSILON_SECONDS) continue;
      if (best === null || turn.startSeconds < best) best = turn.startSeconds;
      continue;
    }
    if (turn.startSeconds >= currentTime - BOUNDARY_EPSILON_SECONDS) continue;
    if (best === null || turn.startSeconds > best) best = turn.startSeconds;
  }
  return best;
}

/**
 * A position held inside the recording.
 *
 * A timestamp past the end clamps rather than failing — a transcript's own last turn can end after
 * the length the probe measured, and a person pressing that turn is asking for the end of the
 * recording, not for an error.
 */
export function clampPlaybackTime(seconds: number, duration: number): number {
  if (!Number.isFinite(seconds)) return 0;
  const ceiling = Number.isFinite(duration) && duration > 0 ? duration : Number.POSITIVE_INFINITY;
  return Math.max(0, Math.min(seconds, ceiling));
}

/** A stored or requested speed, held to the ladder the menu offers. */
export function coercePlaybackRate(value: unknown): PlaybackRate {
  const rate = typeof value === "number" ? value : Number.parseFloat(String(value));
  const match = PLAYBACK_RATES.find((candidate) => candidate === rate);
  return match ?? DEFAULT_PLAYBACK_RATE;
}

/**
 * `1.0×`, the way the speed control and its menu label a speed.
 *
 * One decimal at least, so `1` reads as a speed rather than as a count, and two at most, so
 * `1.25` keeps its quarter — which is exactly what "at least one, at most two" produces for every
 * rung of the ladder.
 */
export function formatPlaybackRate(rate: number): string {
  const digits = Number.isInteger(rate * 10) ? 1 : 2;
  return `${rate.toFixed(digits)}×`;
}
